import { StatCard } from '@/components/StatCard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, Loader2, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import type { NotOnboardedOrganization } from '@octave/contracts';
import {
    useNotOnboardedOrganizations,
} from '../hooks/useOrganization';
import { useCreateOrganization } from '../hooks/useOrganizationActions';

interface Site {
  id?: string;
  name: string;
  address: {
    street: string;
    zipCode: string;
    city: string;
    country: string;
  };
}

interface OrganizationDraft {
  id: string;
  name: string;
  slug?: string;
  sites: Site[];
}

const siteSchema = z.object({
  id: z.string().optional(),
  name: z
    .string()
    .min(1, { message: 'Le nom du centre de facturation est requis.' }),
  address: z.object({
    street: z.string().min(1, { message: 'La rue est requise.' }),
    zipCode: z.string().min(1, { message: 'Le code postal est requis.' }),
    city: z.string().min(1, { message: 'La ville est requise.' }),
    country: z.string().min(1, { message: 'Le pays est requis.' }),
  }),
});

const organizationSchema = z.object({
  id: z.string(),
  name: z.string().min(1, { message: "Le nom de l'organisation est requis." }),
  slug: z.string().min(1, { message: 'Le slug est requis.' }),
  sites: z.array(siteSchema).min(0),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

export function OnboardingView() {
  const [selectedOrgData, setSelectedOrgData] =
    useState<OrganizationDraft | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const {
    data: organizations,
    isLoading,
    isError,
  } = useNotOnboardedOrganizations();

  const { mutate: createOrganization, isPending } = useCreateOrganization();

  const filteredOrganizations = useMemo(() => {
    if (!organizations) return [];
    if (!searchText) return organizations;
    const lower = searchText.toLowerCase();
    return organizations.filter((org) =>
      org.name.toLowerCase().includes(lower)
    );
  }, [organizations, searchText]);

  const form = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      id: '',
      name: '',
      slug: '',
      sites: [],
    },
  });

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[éèêë]/g, 'e')
      .replace(/[àâä]/g, 'a')
      .replace(/[ùûü]/g, 'u')
      .replace(/[îï]/g, 'i')
      .replace(/[ôö]/g, 'o')
      .replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  useEffect(() => {
    if (selectedOrgData) {
      form.reset({
        id: selectedOrgData.id,
        name: selectedOrgData.name,
        slug: selectedOrgData.slug || generateSlug(selectedOrgData.name),
        sites: selectedOrgData.sites || [],
      });
    } else {
      form.reset({ id: '', name: '', slug: '', sites: [] });
    }
  }, [selectedOrgData, form]);

  const handleIntegrateClick = (org: NotOnboardedOrganization) => {
    setSelectedOrgData({
      ...org,
      sites: org.sites.map((site) => ({
        ...site,
        address: {
          street: site.address?.street ?? '',
          zipCode: site.address?.zipCode ?? '',
          city: site.address?.city ?? '',
          country: site.address?.country ?? '',
        },
      })),
    });
    setIsModalOpen(true);
  };

  const onSubmit = (data: OrganizationFormData) => {
    createOrganization(
      {
        name: data.name,
        externalOrganizationId: data.id,
        tenantSlug: data.slug,
        sites: data.sites.map((site) => ({
          name: site.name,
          externalSiteId: site.id || '',
          address: site.address,
        })),
      },
      {
        onSuccess: () => {
          toast.success("L'organisation a été intégrée avec succès");
          setIsModalOpen(false);
        },
        onError: () => {
          toast.error(
            "Une erreur est survenue lors de l'intégration de l'organisation"
          );
        },
      }
    );
  };

  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedOrgData(null);
    }
    setIsModalOpen(open);
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div>
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-4 w-96 mt-2" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Skeleton className="h-32 rounded-2xl" />
        </div>
        <Skeleton className="h-10 w-full sm:w-80" />
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-[72px] w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Intégration des organisations
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez l'intégration des nouvelles organisations à la plateforme
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
          <Building2 className="h-10 w-10 mb-3 text-slate-300" />
          <p className="text-sm font-medium text-red-500">
            Erreur lors du chargement des organisations
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            className="mt-4 bg-white border-slate-200"
          >
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Intégration des organisations
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Gérez l'intégration des nouvelles organisations à la plateforme
        </p>
      </div>

      {/* Stat Card */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Building2}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          label="Organisations à intégrer"
          value={organizations?.length ?? 0}
        />
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher une organisation..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9 bg-white border-slate-200"
          />
        </div>
      </div>

      {/* Organizations List */}
      <div className="space-y-2">
        {filteredOrganizations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Building2 className="h-10 w-10 mb-3 text-slate-300" />
            <p className="text-sm font-medium">
              Aucune organisation à intégrer
            </p>
            <p className="text-xs mt-1">
              {searchText
                ? 'Essayez de modifier votre recherche'
                : 'Toutes les organisations ont été intégrées'}
            </p>
          </div>
        ) : (
          filteredOrganizations.map((org) => (
            <div
              key={org.id}
              className="group flex items-center gap-4 bg-white rounded-xl px-5 py-4 transition-all duration-200 hover:shadow-md border border-transparent hover:border-emerald-100"
            >
              {/* Icon */}
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 group-hover:bg-amber-50 transition-colors">
                <Building2 className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
              </div>

              {/* Main info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {org.name}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {org.sites?.length || 0} centre{(org.sites?.length || 0) > 1 ? 's' : ''} de facturation
                </p>
              </div>

              {/* Integrate button */}
              <Button
                size="sm"
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all shrink-0"
                onClick={() => handleIntegrateClick(org)}
              >
                <Plus className="h-3.5 w-3.5" />
                Intégrer
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Integration Dialog */}
      <Dialog open={isModalOpen} onOpenChange={handleModalOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              Intégrer {form.watch('name') || 'Organisation'}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Détails de l'organisation
                </h3>
                <FormField
                  control={form.control}
                  name="id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        ID de l'organisation
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          readOnly
                          className="bg-slate-50 border-slate-200 text-slate-500"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Nom de l'organisation
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Nom..."
                          {...field}
                          className="bg-white border-slate-200"
                          onChange={(e) => {
                            field.onChange(e);
                            if (e.target.value) {
                              const generatedSlug = generateSlug(
                                e.target.value
                              );
                              form.setValue('slug', generatedSlug, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Identifiant de l'organisation
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="slug-de-organisation"
                          {...field}
                          className="bg-white border-slate-200"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Centres de facturation
                </h3>

                {form.watch('sites').map((_, index) => (
                  <div
                    key={index}
                    className="space-y-4 p-4 border border-slate-200 rounded-xl bg-slate-50"
                  >
                    <FormField
                      control={form.control}
                      name={`sites.${index}.name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-600">
                            Nom du centre de facturation
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Nom du centre de facturation..."
                              {...field}
                              className="bg-white border-slate-200"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="space-y-4 mt-4">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        Adresse
                      </h4>

                      <FormField
                        control={form.control}
                        name={`sites.${index}.address.street`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-slate-600">
                              Rue
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Numéro et nom de rue..."
                                {...field}
                                className="bg-white border-slate-200"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name={`sites.${index}.address.zipCode`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-slate-600">
                                Code postal
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Code postal..."
                                  {...field}
                                  className="bg-white border-slate-200"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`sites.${index}.address.city`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-slate-600">
                                Ville
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Ville..."
                                  {...field}
                                  className="bg-white border-slate-200"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name={`sites.${index}.address.country`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-slate-600">
                              Pays
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Pays..."
                                {...field}
                                className="bg-white border-slate-200"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                ))}

                {form.formState.errors.sites?.message && (
                  <p className="text-sm font-medium text-destructive">
                    {form.formState.errors.sites.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleModalOpenChange(false)}
                  disabled={isPending}
                  className="border-slate-200"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={isPending}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Confirmer l'intégration"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
